import React from 'react';
import { Button, Card, Flex, Row, Col, Typography, message, Switch, Select, Spin, Input } from 'antd';
import { ChevronDown, Mail, Save } from 'lucide-react';
import { CarouselVersionType, ScrollType, ThemeType, WidgetSettings } from '../../../types/widget-settings';
import { useWidgetSettings } from '../hooks/settings/use-widget-settings';

const SETTINGS_LABEL_STYLE: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 15,
  color: '#2e2e2e'
};

const isValidEmail = (email: string) => {
  if (!email) return true; // Email vacío es válido (opcional)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const PersonalSettingsPage: React.FC = () => {
  const { saveSettings, getSettings } = useWidgetSettings();

  const [isSaving, setIsSaving] = React.useState(false);
  const [settings, setSettings] = React.useState<WidgetSettings>({
    type: 'STANDARD',
    title: 'Configuración personal',
    handle: '',
    avatar: '',
    hideAvatar: false,
    hideTitle: false,
    hideArrows: true,
    scrollDirection: 'horizontal',
    theme: 'light',
    carouselVersion: 'v1',
    notificationEmail: ''
  });

  const getClientSettings = React.useCallback(async () => {
    const res = await getSettings();
    if (res && 'type' in res) {
      setSettings({
        type: res.type,
        title: res.title || 'Configuración personal',
        handle: res.handle,
        avatar: res.avatar,
        hideAvatar: !!res.hideAvatar,
        hideTitle: !!res.hideTitle,
        hideArrows: !!res.hideArrows,
        scrollDirection: (res.scrollDirection as ScrollType) || 'horizontal',
        theme: (res.theme as ThemeType) || 'light',
        carouselVersion: (res.carouselVersion as CarouselVersionType) || 'v1',
        notificationEmail: res.notificationEmail || ''
      });
    } else {
      message.error('No se pudo obtener la configuración. Intenta nuevamente.');
    }
  }, [getSettings]);

  const saveClientSettings = async () => {
    setIsSaving(true);
    try {
      const res = await saveSettings(settings);
      if (res) {
        message.success('Configuración guardada correctamente');
      } else {
        message.error('No se pudo guardar la configuración. Intenta nuevamente.');
      }
    } catch (error) {
      console.error('Error al guardar configuración cliente:', error);
      message.error('No se pudo guardar la configuración. Intenta nuevamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = (key: keyof typeof settings) => (checked: boolean) => {
    setSettings(prev => ({ ...prev, [key]: !checked }));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({ ...prev, notificationEmail: e.target.value }));
  };

  React.useEffect(() => {
    getClientSettings();
  }, [getClientSettings]);

  return (
    <Flex justify='center'>
      <Row gutter={[16, 16]} style={{ width: '100%', maxWidth: 700 }}>
        <Col span={24} style={{ marginBottom: '20px' }}>
          <Typography.Title level={2} style={{ textAlign: 'center', marginBottom: '30px' }}>
            Configuración personal
          </Typography.Title>

          {/* Email */}
          <Card style={{ backgroundColor: '#f8f9ff', display: 'flex', justifyContent: 'center' }}>
            <Flex vertical gap='middle'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Email de contacto</Typography.Text>
              <Input
                placeholder='tu-email@ejemplo.com'
                value={settings.notificationEmail}
                onChange={handleEmailChange}
                prefix={<Mail width={14} />}
                status={settings.notificationEmail && !isValidEmail(settings.notificationEmail) ? 'error' : ''}
                style={{ maxWidth: 350 }}
              />
              <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                Email para comunicaciones y notificaciones del sistema
              </Typography.Text>
              {settings.notificationEmail && !isValidEmail(settings.notificationEmail) && (
                <Typography.Text type='danger' style={{ fontSize: 12 }}>
                  Por favor ingresa un email válido
                </Typography.Text>
              )}
            </Flex>
          </Card>

          {/* Handle (solo lectura) */}
          <Card style={{ marginTop: 16 }}>
            <Flex vertical gap='small'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Handle</Typography.Text>
              <Input
                addonBefore='@'
                value={settings.handle}
                disabled
                className='handle-input'
                style={{
                  maxWidth: 350,
                  backgroundColor: '#f5f5f5',
                  textAlign: 'center',
                  margin: '0 auto'
                }}
              />

              <style>
                {`
                  .handle-input .ant-input {
                    text-align: center;
                  }
                  .handle-input .ant-input:not(:placeholder-shown) {
                    text-indent: -32px;
                  }
                  .handle-input .ant-input::placeholder {
                    text-align: center;
                  }
                `}
              </style>

              <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                Cuenta de Instagram del cliente (no se puede modificar)
              </Typography.Text>
            </Flex>
          </Card>

          {/* Toggles */}
          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Mostrar avatar</Typography.Text>
              <Switch checked={!settings.hideAvatar} onChange={handleToggle('hideAvatar')} />
            </Flex>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Mostrar título (username)</Typography.Text>
              <Switch checked={!settings.hideTitle} onChange={handleToggle('hideTitle')} />
            </Flex>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Mostrar flechas de navegación</Typography.Text>
              <Switch checked={!settings.hideArrows} onChange={handleToggle('hideArrows')} />
            </Flex>
          </Card>

          {/* Selectores */}
          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Dirección del scroll</Typography.Text>
              <Select
                value={settings.scrollDirection}
                onChange={value => setSettings(prev => ({ ...prev, scrollDirection: value }))}
                style={{ width: 120 }}
                suffixIcon={<ChevronDown width={12} />}
              >
                <Select.Option value='horizontal'>Horizontal</Select.Option>
                <Select.Option value='vertical'>Vertical</Select.Option>
              </Select>
            </Flex>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Theme</Typography.Text>
              <Select
                value={settings.theme}
                onChange={value => setSettings(prev => ({ ...prev, theme: value }))}
                style={{ width: 120 }}
                suffixIcon={<ChevronDown width={12} />}
              >
                <Select.Option value='light'>Light</Select.Option>
                <Select.Option value='dark'>Dark</Select.Option>
              </Select>
            </Flex>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Versión del carrusel</Typography.Text>
              <Select
                value={settings.carouselVersion}
                onChange={value => setSettings(prev => ({ ...prev, carouselVersion: value }))}
                style={{ width: 120 }}
                suffixIcon={<ChevronDown width={12} />}
              >
                <Select.Option value='v1'>V1</Select.Option>
                <Select.Option value='v2'>V2</Select.Option>
              </Select>
            </Flex>
          </Card>

          {/* Guardar */}
          <Button
            onClick={saveClientSettings}
            icon={isSaving ? <Spin size='small' /> : <Save width={15} />}
            disabled={!!(settings.notificationEmail && !isValidEmail(settings.notificationEmail))}
            style={{
              width: '200px',
              height: '45px',
              marginTop: '32px',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            {isSaving ? 'Guardando...' : 'Guardar Configuración'}
          </Button>
        </Col>
      </Row>
    </Flex>
  );
};
